
import React from 'react';
import { SimulationConfig, ShapeType, RotationMode } from '@/types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { Lock as LucideLock } from 'lucide-react';

interface ShapeSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  getLockInfo?: (id: string) => { isLocked: boolean; min_plan?: string };
}

export const ShapeSettingsSection: React.FC<ShapeSettingsSectionProps> = ({ config, setConfig, isLocked, getLockInfo }) => {
  const shapes: ShapeType[] = [
      'CIRCLE', 'SQUARE', 'TRIANGLE', 'PENTAGON', 'HEXAGON', 'HEPTAGON', 'OCTAGON', 
      'NONAGON', 'DECAGON', 'STAR_5', 'STAR_6', 'STAR_8', 'HEART', 'DIAMOND', 
      'CROSS', 'CLOVER', 'FLOWER', 'SHIELD', 'INVERTED_TRIANGLE', 'GEM', 'GEAR'
  ];

  const ringLocked = getLockInfo?.('ringPattern').isLocked;

  const toggleMorphShape = (shape: ShapeType) => {
    setConfig(prev => {
        const current = prev.morphableShapes || shapes;
        if (current.includes(shape)) {
            if (current.length <= 1) return prev; 
            return { ...prev, morphableShapes: current.filter(s => s !== shape) };
        } else {
            return { ...prev, morphableShapes: [...current, shape] };
        }
    });
  };

  return (
    <div className={`w-full flex flex-col gap-3 p-4 rounded-3xl border-2 bg-indigo-600/10 border-indigo-500/30 mb-4 shadow-lg shadow-indigo-500/5 relative`}>
      <div className="flex flex-col gap-2 p-3 bg-black/10 rounded-2xl border border-indigo-500/20 relative">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase text-indigo-400">Container Geometry</span>
            {ringLocked && <LucideLock className="w-3 h-3 text-red-500" />}
          </div>
          <ControlGroup label="Outer Shape" settingKey="containerShape" disabled={config.mode === 'SPACE_SPIRAL'} disabledMessage="Container shape is fixed in Space Spiral mode" darkMode={config.darkMode}>
            <div className="flex items-center justify-end border border-white/5 p-2 rounded-xl">
                <select 
                    value={config.containerShape || 'CIRCLE'} 
                    onChange={e => setConfig(p => ({...p, containerShape: e.target.value as ShapeType}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                    disabled={config.mode === 'SPACE_SPIRAL'}
                >
                    {shapes.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
            </div>
          </ControlGroup>

          <ControlGroup label="Default Starting Shape (On Reset)" settingKey="initialContainerShape" darkMode={config.darkMode}>
            <div className="flex items-center justify-end border border-white/5 p-2 rounded-xl">
                <select 
                    value={config.initialContainerShape || 'CIRCLE'} 
                    onChange={e => setConfig(p => ({...p, initialContainerShape: e.target.value as ShapeType}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                >
                    {shapes.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
            </div>
          </ControlGroup>
          
          <SmartInput 
            label="Shape Angle" 
            min={0} max={360} 
            value={config.containerRotation || 0} 
            onChange={v => setConfig(p => ({...p, containerRotation: v}))} 
            darkMode={config.darkMode} 
            settingKey="containerRotation"
            disabled={config.mode === 'SPACE_SPIRAL'}
            disabledMessage="Rotation is managed by the Spiral Engine in this mode"
          />

          <ControlGroup label="Auto Morphing" settingKey="shapeMorphEnabled" darkMode={config.darkMode} disabled={config.mode === 'SPACE_SPIRAL'}>
            <div className="flex items-center justify-end border border-white/5 p-2 rounded-xl">
                <Toggle 
                    active={config.shapeMorphEnabled} 
                    onClick={() => setConfig(p => ({...p, shapeMorphEnabled: !p.shapeMorphEnabled}))} 
                    color="bg-indigo-500" 
                    disabled={config.mode === 'SPACE_SPIRAL'}
                />
            </div>
          </ControlGroup>

          {config.shapeMorphEnabled && (
              <>
                  <ControlGroup label="Continuous Shift" settingKey="shapeMorphContinuous" darkMode={config.darkMode}>
                    <div className="flex items-center justify-end border border-white/5 p-2 rounded-xl">
                        <Toggle active={config.shapeMorphContinuous} onClick={() => setConfig(p => ({...p, shapeMorphContinuous: !p.shapeMorphContinuous}))} color="bg-indigo-400" />
                    </div>
                  </ControlGroup>
                  
                  {!config.shapeMorphContinuous && (
                      <SmartInput label="Change Interval" min={0.1} max={5.0} step={0.1} value={config.shapeMorphSpeed} onChange={v => setConfig(p => ({...p, shapeMorphSpeed: v}))} darkMode={config.darkMode} settingKey="shapeMorphSpeed" />
                  )}
                  <SmartInput label="Morph Duration (ms)" min={100} max={3000} step={100} value={config.shapeMorphDuration} onChange={v => setConfig(p => ({...p, shapeMorphDuration: v}))} darkMode={config.darkMode} settingKey="shapeMorphDuration" />

                  <ControlGroup label="Morph Cycle Pool" settingKey="morphableShapes" darkMode={config.darkMode}>
                    <div className="grid grid-cols-4 gap-1 w-full bg-black/20 p-2 rounded-xl">
                        {shapes.map(s => {
                            const isActive = (config.morphableShapes || shapes).includes(s);
                            return (
                                <button 
                                  key={s} 
                                  onClick={() => toggleMorphShape(s)}
                                  className={`text-[7px] font-bold py-1 px-1 rounded transition-all ${isActive ? 'bg-indigo-500 text-white' : 'bg-white/5 text-neutral-500 hover:bg-white/10'}`}
                                >
                                    {s.replace('_', ' ')}
                                </button>
                            );
                        })}
                    </div>
                  </ControlGroup>
              </>
          )}
      </div>

      <div className="flex flex-col gap-2 p-3 bg-black/10 rounded-2xl border border-pink-500/20 mt-2">
          <span className="text-[10px] font-black uppercase text-pink-400">Object Geometry</span>
          <ControlGroup label="Ball Shape" settingKey="ballShape" darkMode={config.darkMode}>
            <div className="flex items-center justify-end border border-white/5 p-2 rounded-xl">
                <select 
                    value={config.ballShape || 'CIRCLE'} 
                    onChange={e => setConfig(p => ({...p, ballShape: e.target.value as ShapeType}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                >
                    {shapes.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
            </div>
          </ControlGroup>

          <ControlGroup label="Rotation Logic" settingKey="ballRotationMode" darkMode={config.darkMode}>
            <div className="flex items-center justify-end border border-white/5 p-2 rounded-xl">
                <select 
                    value={config.ballRotationMode || 'VELOCITY'} 
                    onChange={e => setConfig(p => ({...p, ballRotationMode: e.target.value as RotationMode}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                >
                    <option value="LOCKED">Locked (0°)</option>
                    <option value="VELOCITY">Follow Velocity</option>
                    <option value="SPIN">Constant Spin</option>
                </select>
            </div>
          </ControlGroup>
      </div>
    </div>
  );
};
